import type { RouterOutputs } from 'shared/api'

/**
 * What the PWA knows about the staff roles (ADR-0029, and the `superadmin` — the
 * owner, 2026-10-28), apart from which
 * sections each sees — that map is the app's (`app/ui/routes.tsx`).
 *
 * Every fact here is presentation only. The backend reads the live staff record
 * and is the real guard; a role that reaches a procedure it may not is refused
 * there, whatever this file says.
 */

type User = RouterOutputs['auth']['Me']
export type Role = User['role']
type Location = RouterOutputs['locations']['List'][number]

/** The type of a place (ADR-0028): a Point, a hub, the central cashbox. */
export type PlaceType = Location['type']

/**
 * Every role, in the order the staff screen offers them, and what each is bound
 * to: an `operator` to one Point, a `hub` worker to a hub; every other role is
 * global and carries no `locationId`.
 */
export const ROLE_BINDING = {
	superadmin: null,
	operator: 'point',
	hub: 'hub',
	driver: null,
	cashier: null,
	support: null,
	admin: null,
} as const satisfies Record<Role, PlaceType | null>

export const STAFF_ROLES = Object.keys(ROLE_BINDING) as Role[]

/** True when the role works at one place and must carry its `locationId`. */
export function isLocationBound(role: Role): boolean {
	return ROLE_BINDING[role] !== null
}

/**
 * Whether the role works as the administrator on a shared screen: the `admin`, and
 * the `superadmin`, who sees every section an admin does and more.
 */
export function actsAsAdmin(role: Role): boolean {
	return role === 'admin' || role === 'superadmin'
}

/**
 * Who picks the Point a point-scoped screen works at. A bound role never picks —
 * its place is on its account; an `admin` is global and picks one.
 */
export function picksPlace(role: Role): boolean {
	return actsAsAdmin(role)
}

/**
 * Who may change a Parcel from its detail: take or cancel a payment, reverse a
 * handover, set or clear a marker. A `cashier` and `support` look a Parcel up but
 * do not edit it here (ADR-0029).
 */
export function editsParcels(role: Role): boolean {
	return actsAsAdmin(role) || role === 'operator'
}

/**
 * The type of a place, read off a Location — the one spot that does so, so every
 * filter by type below follows it.
 */
export function placeTypeOf(location: Location): PlaceType {
	return location.type
}

/** The places a role may be bound to — Points for an operator, hubs for a hub worker. */
export function placesFor(role: Role, locations: readonly Location[]): Location[] {
	const type = ROLE_BINDING[role]
	return type ? locations.filter((l) => placeTypeOf(l) === type) : []
}
